<script setup lang="ts">
interface IProps {
    offsetTop?: number;
    stopElement?: string;
    offsetStop?: number;
    disabled?: boolean;
    element?: string;
    zIndex?: number;
}
const {
    offsetTop = 0,
    stopElement = '#__footer',
    offsetStop = 0,
    disabled,
    element = 'div',
    zIndex = 889,
} = defineProps<IProps>();
const emit = defineEmits<{
    'update:fix': [fixed: boolean];
}>();

const computedZIndex = computed(() => (disabled ? 'none' : zIndex));
const globalDefaultStopElement = '#__footer';
const actual = ref<HTMLDivElement>();
const fake = ref<HTMLDivElement>();
const heightSizer = ref<HTMLDivElement>();
const sticky = ref(false);
const stopped = ref(false);
const height = ref(0);
const maxWidth = ref(0);
const absolutePosition = ref(0);
const windowHeight = ref(0);
const stopElementSelector = ref(stopElement);
const scrollListener = ref(() => {});

const reset = () => {
    sticky.value = false;
    stopped.value = false;
    height.value = 0;
    maxWidth.value = 0;
    absolutePosition.value = 0;
    windowHeight.value = 0;
    updateSizes();
    validateStopElement();
    initStickyHeader();
    scrollListener.value();
    onResize();
};

watch([sticky, () => disabled], ([sticky, disabled]) => {
    if (!disabled) {
        emit('update:fix', sticky);
    }
});

const scrollable = computed(
    () => height.value > windowHeight.value - offsetTop
);

const maxHeight = computed(() => Math.max(windowHeight.value - offsetTop, 0));

const initStickyHeader = () => {
    let prevScrollPos = window.scrollY;
    // why a function stored as a ref ?
    scrollListener.value = () => {
        const stop = document.querySelector(
            stopElementSelector.value
        ) as HTMLElement | null;
        const actualTop = actual.value?.getBoundingClientRect().top || 0;
        const fakeTop = fake.value?.getBoundingClientRect().top || 0;
        const stopTop = stop?.getBoundingClientRect().top || 0;
        const fixedScrolledOffset =
            (actual.value?.scrollTop || 0) -
            (heightSizer.value?.scrollTop || 0);
        if (!sticky.value) {
            sticky.value = actualTop <= offsetTop;
        } else {
            sticky.value = fakeTop <= offsetTop;
        }
        stopped.value =
            stopTop <=
            height.value + offsetStop + offsetTop - fixedScrolledOffset;
        absolutePosition.value =
            stopTop -
            fakeTop -
            document.documentElement.clientTop -
            height.value +
            (fake.value?.offsetTop || 0) -
            offsetStop;
        if (sticky.value && scrollable.value) {
            actual.value?.scrollBy({
                top: window.scrollY - prevScrollPos,
            });
        }
        prevScrollPos = window.scrollY;
    };
};
const updateSizes = () => {
    height.value = heightSizer.value?.offsetHeight || 0;
    maxWidth.value = heightSizer.value?.offsetWidth || 0;
};
const onResize = () => {
    windowHeight.value = window.innerHeight;
};

const validateStopElement = () => {
    const el = document.querySelector(stopElement) as HTMLElement | null;
    const offsetParent = el?.offsetParent;
    const invalidEl = offsetParent === undefined || offsetParent === null;
    if (stopElementSelector.value !== globalDefaultStopElement && invalidEl) {
        console.warn(
            `Stop element selector ${stopElement} not found. It may be wrong or hidden based on data. Fallback to ${globalDefaultStopElement}.`
        );
        stopElementSelector.value = globalDefaultStopElement;
    } else if (
        stopElementSelector.value === globalDefaultStopElement &&
        stopElement !== globalDefaultStopElement &&
        !invalidEl
    ) {
        stopElementSelector.value = stopElement;
    }
};

onMounted(() => {
    updateSizes();
    validateStopElement();
    initStickyHeader();
    scrollListener.value();
    onResize();
    window.addEventListener('scroll', scrollListener.value, false);
    window.addEventListener('resize', onResize);
});
onUpdated(() =>
    nextTick(() => {
        updateSizes();
        validateStopElement();
    })
);
onBeforeUnmount(() => {
    window.removeEventListener('scroll', scrollListener.value);
    window.removeEventListener('resize', onResize);
});
</script>
<template>
    <div
        class="mr-fix-root"
        :style="{
            '--top-offset': `${offsetTop}px`,
            '--bottom-offset': `${offsetStop}px`,
            '--width': `${maxWidth}px`,
            '--height': `${height}px`,
            '--absolute': `${absolutePosition}px`,
            '--max-height': `${maxHeight}px`,
        }"
    >
        <component
            :is="element"
            ref="actual"
            class="actual"
            :class="{ sticky, stopped, disabled, scrollable }"
        >
            <div ref="heightSizer" class="slot-wrapper">
                <slot :adjustment="reset" />
            </div>
        </component>
        <div v-show="sticky && !disabled" ref="fake" class="fake" />
    </div>
</template>

<style lang="scss" scoped>
.mr-fix-root {
    position: relative;
    pointer-events: none;
    z-index: v-bind(computedZIndex);
    .actual {
        pointer-events: all;
        &.sticky:not(.disabled) {
            position: fixed;
            top: var(--top-offset);
            width: var(--width);
            &.stopped {
                position: absolute;
                top: var(--absolute);
            }
            &.scrollable:not(.stopped) {
                max-height: var(--max-height);
                @include no-scrollbar;
                overflow: scroll;
            }
        }
    }
    .fake {
        height: var(--height);
    }
}
</style>
